import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useT } from "../i18n";
import { Marke, euro } from "../bausteine";
import { Zeichen } from "../bausteine/Zeichen";
import * as R from "../daten/routen";
import { useBelege, zustandsart, type Beleg } from "../daten/belege";
import type { Kunde, Vorgang } from "../daten/typen";
import { fokusVorher, sucheFeldAnmelden, sucheOeffnen, sucheSchliessen, useSucheOffen } from "./suchschalter";
import { useZuletzt, zuletztFuer, zuletztMerken, type Zuletzt } from "../daten/zuletzt";
import { useSitzung } from "./Sitzung";
import "../stile/suche.css";

/* ══════════════════════════════════════════════════════════════════
   Die globale Suche (18.09.2026).

   Luca: *„Ich fände es cool, wenn wir eine globale Suche haben, aber auch
   ein Suchmodul."* Das Suchmodul ist `/belege`. Das hier ist die andere
   Hälfte: **ein Feld über jeder Maske**, das alles findet, was eine
   Nummer oder einen Namen hat — und von dort in einem Griff hinführt.

   Drei Quellen, eine Liste:

   - **Belege** — live aus Lexware Office, über denselben Weg wie das
     Suchmodul (`GET /belege`). Der Server hält die Liste eine Minute,
     deshalb kostet das Tippen drüben nichts.
   - **Kunden** und **Vorgänge** — aus MrMachine, über die Suchwege, die
     die Listen schon haben (`GET /kunden?suche=`, `GET /vorgaenge?suche=`).
     Kein eigener Suchindex: Eine zweite Suche, die anders findet als die
     Liste, ist eine zweite Wahrheit.

   Jede Quelle antwortet für sich. Hängt Lexware, stehen Kunden und
   Vorgänge trotzdem da — und die Gruppe sagt, dass drüben noch gefragt
   wird oder niemand antwortet.

   **Tastatur zuerst.** `Strg`/`⌘` + `K` von überall, `/` außerhalb eines
   Feldes; Pfeile wählen, Enter öffnet, Esc schließt. Am Telefon geht sie
   über das Zeichen im Kopf auf und füllt den Schirm.
   ══════════════════════════════════════════════════════════════════ */

const RUHE_MS = 250;
const BELEGE_HOECHSTENS = 6;
const KUNDEN_HOECHSTENS = 5;
const VORGAENGE_HOECHSTENS = 5;

function useRuhig(wert: string, ms: number): string {
  const [ruhig, setzeRuhig] = useState(wert);
  useEffect(() => {
    const uhr = window.setTimeout(() => setzeRuhig(wert), ms);
    return () => window.clearTimeout(uhr);
  }, [wert, ms]);
  return ruhig;
}

/** `2026-09-18` → `18.09.2026`. */
function datumText(iso: string): string {
  const [j, m, t] = iso.split("-");
  return j && m && t ? `${t}.${m}.${j}` : iso;
}

const ANGEBOTSZUSTAENDE: Vorgang["zustand"][] = ["entwurf", "angebot", "abgelehnt"];

/**
 * Der Knopf im Kopf und das Tastenkürzel — und, solange sie offen ist,
 * die Suche selbst.
 *
 * **Nur, wer Belege sehen darf** (`schale/rechte.ts`, Zeile `/belege`).
 * Der rein technische Außendienst sieht keine Preise und keine Belege
 * (17-planungsschnitt.md); ihm einen Knopf zu zeigen, hinter dem jede
 * Gruppe mit 403 antwortet, wäre ein Knopf, der nichts tut.
 */
export function SucheKnopf({ feld = false }: { feld?: boolean } = {}) {
  const t = useT();
  const { darfAuf } = useSitzung();
  const erlaubt = darfAuf("/belege");

  useEffect(() => {
    if (!erlaubt) return;
    const kuerzel = (e: globalThis.KeyboardEvent) => {
      /* **`Strg`/`⌘` + `K` gilt auch im Eingabefeld** — so machen es die
         Anwendungen, aus denen man es kennt, und kein Browser braucht es
         für etwas, das man hier vermissen würde. `/` dagegen nur außerhalb
         eines Feldes: Wer tippt, will einen Schrägstrich. */
      const ziel = e.target as HTMLElement | null;
      const tippt = ziel?.isContentEditable
        || ["INPUT", "TEXTAREA", "SELECT"].includes(ziel?.tagName ?? "");
      const mitK = (e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "k" || e.key === "K");
      const schraeg = e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !tippt;
      if (!mitK && !schraeg) return;
      e.preventDefault();
      sucheOeffnen();
    };
    document.addEventListener("keydown", kuerzel);
    return () => document.removeEventListener("keydown", kuerzel);
  }, [erlaubt]);

  if (!erlaubt) return null;
  /* **Am Schreibtisch ein echtes Feld in der Mitte des Kopfs** (07.10.2026,
     Signal, docs/64). Erst war es ein Knopf, der das Suchfenster öffnete —
     Luca: *„Die Suchleiste oben soll keine Pop-up-Suchleiste aufmachen,
     sondern die bestehende, sodass man einfach nur dadrin eintippen kann."*
     Jetzt tippt man hinein, und die Treffer klappen darunter auf. Dieselben
     Quellen wie im Fenster (`useSuchliste`), also keine zweite Wahrheit. Am
     Telefon und auf dem Tablett bleibt die Lupe mit dem Fenster. */
  if (feld) return <KopfSuchfeld />;
  return (
    <button
      type="button"
      className="kopf-nachschlagen kopf-suche"
      onClick={sucheOeffnen}
      /* **Die schlichte Lupe, und nur sie** (18.09.2026). Sie ist das
         Zeichen, das jeder ohne Erklärung als Suche liest — daran wird nicht
         gerüttelt. Das Wort daneben fällt weg; es stand neben dem Wort
         „Nachschlagen" und sagte dasselbe (`schale/Schale.tsx` sagt, warum).
         Was der Knopf tut, steht in `title` und `aria-label`. */
      title={t("suche.kuerzel")}
      aria-label={t("suche.kuerzel")}
      aria-keyshortcuts="Control+K Meta+K /"
    >
      <Zeichen name="suche" />
    </button>
  );
}

type Eintrag = {
  schluessel: string;
  gruppe: "belege" | "kunden" | "vorgaenge" | "zuletzt";
  titel: ReactNode;
  unter: ReactNode;
  rechts?: ReactNode;
  oeffnen: () => void;
};

type Gruppe = { id: Eintrag["gruppe"]; kopf: string; laedt: boolean; fehlt: boolean };

/**
 * Was die Suche zu einem Wort findet — dieselben drei Quellen für das
 * Fenster (Telefon, Tablett) und das Feld im Kopf (Schreibtisch).
 * `schliessen` sagt, was vor dem Sprung geschieht: das Fenster zu, das
 * Feld leer.
 */
function useSuchliste(wort: string, schliessen: () => void) {
  const t = useT();
  const gehZu = useNavigate();
  const ruhig = useRuhig(wort.trim(), RUHE_MS);
  const an = ruhig.length >= 2;

  const belege = useBelege(
    { arten: [], wort: ruhig, kunde: "", von: "", bis: "", zustand: "", hoechstens: BELEGE_HOECHSTENS },
    an,
  );
  const kunden = useQuery({
    queryKey: ["suche", "kunden", ruhig],
    queryFn: () => R.kunden(ruhig),
    enabled: an,
    staleTime: 30_000,
  });
  const vorgaenge = useQuery({
    queryKey: ["suche", "vorgaenge", ruhig],
    queryFn: () => R.vorgaengeListe(undefined, undefined, ruhig),
    enabled: an,
    staleTime: 30_000,
  });

  const hin = (weg: () => void) => {
    schliessen();
    weg();
  };

  const belegEintrag = (b: Beleg): Eintrag => ({
    schluessel: `b:${b.id}`,
    gruppe: "belege",
    titel: <><span className="gsuche-nr">{b.nummer || t("belege.ohneNummer")}</span> {b.name}</>,
    unter: [t(`belege.art.${b.art}`), b.datum ? datumText(b.datum) : "", b.vorgangNr].filter(Boolean).join(" · "),
    rechts: (
      <>
        <Marke art={zustandsart(b)}>{t(`belege.zustaende.${b.zustand}`)}</Marke>
        {b.summe !== null ? <span className="geld">{euro(b.summe)}</span> : null}
      </>
    ),
    // Auf die Belegsuche, mit dem Beleg gewählt — dort stehen PDF,
    // Lexware Office und der Vorgang dahinter.
    // `zeitraum: "alle"`: Die Belegsuche nimmt sonst das laufende Jahr, und
    // ein Beleg vom letzten Dezember stünde dort nicht in der Liste.
    oeffnen: () => hin(() => void gehZu({
      to: "/belege", search: { wort: b.nummer || ruhig, beleg: b.id, zeitraum: "alle" },
    })),
  });
  const kundeEintrag = (k: Kunde): Eintrag => {
    const unter = [t(`suche.kundenart.${k.art}`), k.ort].filter(Boolean).join(" · ");
    return {
      schluessel: `k:${k.nr}`,
      gruppe: "kunden",
      titel: <><span className="gsuche-nr">{k.nr}</span> {k.name}</>,
      unter,
      oeffnen: () => hin(() => {
        zuletztMerken({ sorte: "kunden", schluessel: k.nr, titel: k.name, unter });
        void gehZu({ to: "/kunden/$nr", params: { nr: k.nr } });
      }),
    };
  };
  const vorgangEintrag = (v: Vorgang): Eintrag => {
    const angebot = ANGEBOTSZUSTAENDE.includes(v.zustand);
    const unter = [t(angebot ? "suche.angebot" : "suche.auftrag"), v.was].filter(Boolean).join(" · ");
    return {
      schluessel: `v:${v.nr}`,
      gruppe: "vorgaenge",
      titel: <><span className="gsuche-nr">{v.nr}</span> {v.kunde}</>,
      unter,
      oeffnen: () => hin(() => {
        zuletztMerken({ sorte: angebot ? "angebote" : "auftraege", schluessel: v.nr, titel: `${v.nr} ${v.kunde}`, unter });
        void gehZu({ to: angebot ? "/angebote/$nr" : "/auftraege/$nr", params: { nr: v.nr } });
      }),
    };
  };

  /* Die Liste wird bei jedem Bild neu gebaut — sie ist höchstens sechzehn
     Zeilen lang, und ein `useMemo` müsste die drei Bauer oben mitführen. */
  const eintraege: Eintrag[] = an
    ? [
        ...(belege.data?.belege ?? []).slice(0, BELEGE_HOECHSTENS).map(belegEintrag),
        ...(kunden.data?.zeilen ?? []).slice(0, KUNDEN_HOECHSTENS).map(kundeEintrag),
        ...(vorgaenge.data?.zeilen ?? []).slice(0, VORGAENGE_HOECHSTENS).map(vorgangEintrag),
      ]
    : [];

  const gruppen: Gruppe[] = [
    { id: "belege", kopf: "suche.gruppeBelege", laedt: belege.isFetching, fehlt: belege.isError },
    { id: "kunden", kopf: "suche.gruppeKunden", laedt: kunden.isFetching, fehlt: kunden.isError },
    { id: "vorgaenge", kopf: "suche.gruppeVorgaenge", laedt: vorgaenge.isFetching, fehlt: vorgaenge.isError },
  ];
  const nichts = an && !eintraege.length && !belege.isFetching && !kunden.isFetching && !vorgaenge.isFetching;

  const alleBelege = () => hin(() => void gehZu({
    to: "/belege", search: { ...(ruhig ? { wort: ruhig } : {}), zeitraum: "alle" },
  }));

  return { ruhig, an, eintraege, gruppen, nichts, alleBelege, hin, gehZu };
}

/** Die Tasten, die Fenster und Feld teilen: Pfeile wählen, Enter öffnet. */
function useWahl(eintraege: Eintrag[], ruhig: string, listeId: string, sonst: () => void) {
  const [aktiv, setzeAktiv] = useState(0);
  // Die Wahl bleibt in der Liste, auch wenn sie kürzer wird.
  useEffect(() => { setzeAktiv(0); }, [ruhig]);
  const gewaehlt = Math.min(aktiv, Math.max(eintraege.length - 1, 0));
  // Die gewählte Zeile bleibt im Bild, wenn die Pfeile über den Rand gehen.
  useEffect(() => {
    document.getElementById(`${listeId}-${gewaehlt}`)?.scrollIntoView({ block: "nearest" });
  }, [gewaehlt, listeId]);
  const pfeil = (e: KeyboardEvent<HTMLElement>): boolean => {
    if (e.key === "ArrowDown") { e.preventDefault(); setzeAktiv(Math.min(gewaehlt + 1, eintraege.length - 1)); return true; }
    if (e.key === "ArrowUp") { e.preventDefault(); setzeAktiv(Math.max(gewaehlt - 1, 0)); return true; }
    if (e.key === "Enter") {
      e.preventDefault();
      const ziel = eintraege[gewaehlt];
      if (ziel) ziel.oeffnen();
      else sonst();
      return true;
    }
    return false;
  };
  return { gewaehlt, setzeAktiv, pfeil };
}

/** Die Gruppen mit ihren Zeilen — im Fenster wie unter dem Feld. */
function Trefferliste({ gruppen, eintraege, listeId, gewaehlt, setzeAktiv }: {
  gruppen: Gruppe[]; eintraege: Eintrag[]; listeId: string;
  gewaehlt: number; setzeAktiv: (i: number) => void;
}) {
  const t = useT();
  return (
    <>
      {gruppen.map((g) => {
        const zeilen = eintraege
          .map((e, i) => ({ e, i }))
          .filter(({ e }) => e.gruppe === g.id);
        if (!zeilen.length && !(g.id === "belege" && (g.laedt || g.fehlt))) return null;
        return (
          <div className="gsuche-gruppe" key={g.id} role="group" aria-label={t(g.kopf)}>
            <div className="gsuche-kopf">
              {t(g.kopf)}
              {g.laedt ? <span className="gsuche-leise">{t("suche.fragt")}</span> : null}
            </div>
            {g.fehlt && !zeilen.length ? <p className="gsuche-satz leise">{t("suche.belegeFehlen")}</p> : null}
            {zeilen.map(({ e, i }) => (
              <div
                key={e.schluessel}
                id={`${listeId}-${i}`}
                role="option"
                aria-selected={i === gewaehlt}
                className={`gsuche-zeile${i === gewaehlt ? " an" : ""}`}
                /* Nur die Maus markiert beim Überfahren (19.09.2026) —
                   dieselbe Regel wie in Kundenwahl und Positionswähler.
                   Safari schickt vor jedem Tipp ein `mousemove`: Die
                   Markierung sprang dabei auf die berührte Zeile und blieb
                   dort, ohne dass jemand gewählt hätte. */
                onPointerMove={(ev) => { if (ev.pointerType === "mouse" && i !== gewaehlt) setzeAktiv(i); }}
                /* `mousedown` statt Klick hält den Fokus im Feld — sonst
                   schlösse das Feld beim Verlassen, bevor der Klick ankommt. */
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={e.oeffnen}
              >
                <span className="gsuche-text">
                  <span className="gsuche-titel">{e.titel}</span>
                  <span className="gsuche-unter">{e.unter}</span>
                </span>
                {e.rechts ? <span className="gsuche-rechts">{e.rechts}</span> : null}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

/** Das Suchfenster (Telefon, Tablett). Zu ist es ein Nichts und fragt nichts ab. */
export function Suche() {
  const offen = useSucheOffen();
  return offen ? <Suchfenster /> : null;
}

function Suchfenster() {
  const t = useT();
  const listeId = useId();
  const feld = useRef<HTMLInputElement>(null);
  const [wort, setzeWort] = useState("");
  const { ruhig, an, eintraege, gruppen, nichts, alleBelege } = useSuchliste(wort, sucheSchliessen);
  const { gewaehlt, setzeAktiv, pfeil } = useWahl(eintraege, ruhig, listeId, () => { if (ruhig) alleBelege(); });

  /* Der Fokus gehört beim Öffnen ins Feld: Wer sucht, will tippen. Beim
     Schließen geht er dorthin zurück, wo er vorher war — sonst landet er auf
     `body`, und wer mit der Tastatur arbeitet, fängt oben an.

     **Erst merken, dann ins Feld** (B-12, 03.10.2026). */
  useEffect(() => {
    const vorher = fokusVorher(document.activeElement);
    feld.current?.focus();
    return () => { vorher?.focus?.(); };
  }, []);

  const taste = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") { e.preventDefault(); sucheSchliessen(); return; }
    pfeil(e);
  };

  return (
    <div className="gsuche-raum">
      <button
        type="button"
        className="gsuche-schleier"
        aria-label={t("allgemein.schliessen")}
        tabIndex={-1}
        onClick={sucheSchliessen}
      />
      <div className="gsuche" role="dialog" aria-modal="true" aria-label={t("suche.schild")} onKeyDown={taste}>
        <div className="gsuche-feld">
          <Zeichen name="suche" />
          <input
            ref={feld}
            type="search"
            value={wort}
            onChange={(e) => setzeWort(e.target.value)}
            placeholder={t("suche.platz")}
            aria-label={t("suche.platz")}
            role="combobox"
            aria-expanded={eintraege.length > 0}
            aria-controls={listeId}
            aria-activedescendant={eintraege.length ? `${listeId}-${gewaehlt}` : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" className="gsuche-zu" onClick={sucheSchliessen} aria-label={t("allgemein.schliessen")}>
            ×
          </button>
        </div>

        <div className="gsuche-inhalt" id={listeId} role="listbox" aria-label={t("suche.schild")}>
          {!an ? <p className="gsuche-satz">{t("suche.anfang")}</p> : null}
          {nichts ? <p className="gsuche-satz">{t("suche.nichts", { wort: ruhig })}</p> : null}
          {an ? <Trefferliste gruppen={gruppen} eintraege={eintraege} listeId={listeId} gewaehlt={gewaehlt} setzeAktiv={setzeAktiv} /> : null}
        </div>

        <div className="gsuche-fuss">
          <button type="button" className="gsuche-alle" onClick={alleBelege}>
            {ruhig ? t("suche.alleBelegeZu", { wort: ruhig }) : t("suche.alleBelege")}
            <Zeichen name="weiter" />
          </button>
          <span className="gsuche-tasten" aria-hidden="true">{t("suche.tasten")}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Das Suchfeld im Kopf (Schreibtisch). Man tippt hinein; die Treffer
 * klappen darunter auf, so breit wie das Feld. Pfeile wählen, Enter
 * öffnet, Esc leert erst und gibt dann den Fokus ab. `Strg`/`⌘` + `K` und
 * `/suche` legen den Fokus hierher (`suchschalter.ts`).
 *
 * Leer und im Fokus zeigt es „Zuletzt geöffnet" — dieselbe Liste wie die
 * Telefonsuche (`daten/zuletzt.ts`), sonst einen Satz, was es findet.
 */
function KopfSuchfeld() {
  const t = useT();
  const listeId = useId();
  const feld = useRef<HTMLInputElement>(null);
  const [wort, setzeWort] = useState("");
  const [offen, setzeOffen] = useState(false);
  const { ich } = useSitzung();
  if (ich) zuletztFuer(ich.id);
  const zuletzt = useZuletzt();

  const zu = () => { setzeWort(""); setzeOffen(false); feld.current?.blur(); };
  const { ruhig, an, eintraege: treffer, gruppen, nichts, alleBelege, hin, gehZu } = useSuchliste(wort, zu);

  const zuletztEintrag = (z: Zuletzt): Eintrag => ({
    schluessel: `z:${z.sorte}:${z.schluessel}`,
    gruppe: "zuletzt",
    titel: z.titel,
    unter: z.unter,
    oeffnen: () => hin(() => {
      zuletztMerken(z);
      if (z.sorte === "kunden") void gehZu({ to: "/kunden/$nr", params: { nr: z.schluessel } });
      else if (z.sorte === "angebote") void gehZu({ to: "/angebote/$nr", params: { nr: z.schluessel } });
      else if (z.sorte === "auftraege") void gehZu({ to: "/auftraege/$nr", params: { nr: z.schluessel } });
      else void gehZu({ to: "/ressourcen" });
    }),
  });
  const leer = !wort.trim();
  const eintraege = leer ? zuletzt.slice(0, 6).map(zuletztEintrag) : treffer;
  const listeGruppen: Gruppe[] = leer
    ? [{ id: "zuletzt", kopf: "suche.zuletzt", laedt: false, fehlt: false }]
    : gruppen;
  const { gewaehlt, setzeAktiv, pfeil } = useWahl(eintraege, leer ? "" : ruhig, listeId, () => { if (ruhig) alleBelege(); });

  /* `Strg`/`⌘` + `K` und `/suche` legen den Fokus hierher, statt ein
     Fenster zu öffnen. */
  useEffect(() => sucheFeldAnmelden(() => {
    feld.current?.focus();
    feld.current?.select();
    setzeOffen(true);
  }), []);

  const taste = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (wort) setzeWort("");
      else zu();
      return;
    }
    if (!offen) setzeOffen(true);
    pfeil(e);
  };

  const zeigen = offen && (leer ? eintraege.length > 0 : true);

  return (
    <div className={`kopf-suchfeld${offen ? " offen" : ""}`}>
      <Zeichen name="suche" />
      <input
        ref={feld}
        type="search"
        className="kopf-suchfeld-eingabe"
        value={wort}
        onChange={(e) => { setzeWort(e.target.value); setzeOffen(true); }}
        onFocus={() => setzeOffen(true)}
        onBlur={() => setzeOffen(false)}
        onKeyDown={taste}
        placeholder={t("suche.platz")}
        aria-label={t("suche.platz")}
        title={t("suche.kuerzel")}
        aria-keyshortcuts="Control+K Meta+K /"
        role="combobox"
        aria-expanded={zeigen}
        aria-controls={listeId}
        aria-activedescendant={zeigen && eintraege.length ? `${listeId}-${gewaehlt}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
      />
      {offen ? null : <kbd aria-hidden="true">{t("suche.kbd")}</kbd>}
      {zeigen ? (
        <div className="kopf-suchliste" id={listeId} role="listbox" aria-label={t("suche.schild")}>
          {!leer && !an ? <p className="gsuche-satz">{t("suche.anfang")}</p> : null}
          {nichts ? <p className="gsuche-satz">{t("suche.nichts", { wort: ruhig })}</p> : null}
          {leer || an
            ? <Trefferliste gruppen={listeGruppen} eintraege={eintraege} listeId={listeId} gewaehlt={gewaehlt} setzeAktiv={setzeAktiv} />
            : null}
          {!leer ? (
            <div className="gsuche-fuss">
              <button
                type="button" className="gsuche-alle"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={alleBelege}
              >
                {ruhig ? t("suche.alleBelegeZu", { wort: ruhig }) : t("suche.alleBelege")}
                <Zeichen name="weiter" />
              </button>
              <span className="gsuche-tasten" aria-hidden="true">{t("suche.tasten")}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
